
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from '../api';
import { getCategoryPath } from '../categories';
import ChatSection from '../components/ChatSection';
import Snackbar from '../components/Snackbar';

import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import CloseIcon from '@mui/icons-material/Close';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import Typography from '@mui/material/Typography';

function toFarsiNumber(input) {
  return String(input).replace(/\d/g, d => '۰۱۲۳۴۵۶۷۸۹'[d]);
}

function toFarsiPrice(num) {
  if (num === undefined || num === null || num === '' || isNaN(num)) return '-';
  return toFarsiNumber(Number(num).toLocaleString('fa-IR'));
}

function getRelativeTime(dateString) {
  const now = new Date();
  const date = new Date(dateString);
  const diffMs = now - date;
  const diffSecs = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffSecs < 60) return 'لحظاتی پیش';
  if (diffMins < 60) return `${diffMins} دقیقه پیش`;
  if (diffHours < 24) return `${diffHours} ساعت پیش`;
  if (diffDays < 7) return `${diffDays} روز پیش`;
  return date.toLocaleDateString('fa-IR');
}

function AdDetailsPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    document.title = 'جزئیات آگهی | آگاه‌جو';
  }, []);

  const [ad, setAd] = useState(null);
  const [imgIdx, setImgIdx] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  const [saved, setSaved] = useState(false);
  const [note, setNote] = useState('');
  const [originalNote, setOriginalNote] = useState('');
  const [noteId, setNoteId] = useState(null);
  const [noteError, setNoteError] = useState(null);
  const [noteLoading, setNoteLoading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [loginWarn, setLoginWarn] = useState(false);

  const [currentUser, setCurrentUser] = useState(() => {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      try { return JSON.parse(userStr); } catch { return null; }
    }
    return null;
  });

  useEffect(() => {
    const fetchAd = async () => {
      try {
        const res = await axios.get(`/ads/${id}/`);
        setAd(res.data);
        if (localStorage.getItem('token')) {
          try {
            const noteRes = await axios.get(`/ad-notes/?ad=${id}`);
            if (Array.isArray(noteRes.data) && noteRes.data.length > 0) {
              setNote(noteRes.data[0].content || '');
              setOriginalNote(noteRes.data[0].content || '');
              setNoteId(noteRes.data[0].id);
            } else {
              setNote(''); setOriginalNote(''); setNoteId(null);
            }
          } catch {
            setNote(''); setOriginalNote('');
          }
        }
        if (res.data.saved_by && currentUser) {
          setSaved(Array.isArray(res.data.saved_by) && res.data.saved_by.includes(currentUser.id));
        } else {
          setSaved(false);
        }
      } catch (error) {
        console.error('Error loading ad:', error);
      }
    };
    fetchAd();
  }, [id]); // eslint-disable-line

  useEffect(() => {
    if (ad && ad.saved_by && currentUser) {
      setSaved(Array.isArray(ad.saved_by) && ad.saved_by.includes(currentUser.id));
    }
  }, [ad, currentUser]);

  // Keyboard navigation for lightbox
  useEffect(() => {
    if (!lightboxOpen) return;
    const handleKey = (e) => {
      if (e.key === 'Escape') setLightboxOpen(false);
      if (e.key === 'ArrowLeft' && ad?.images?.length > 1)
        setImgIdx(i => (i + 1) % ad.images.length);
      if (e.key === 'ArrowRight' && ad?.images?.length > 1)
        setImgIdx(i => (i - 1 + ad.images.length) % ad.images.length);
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [lightboxOpen, ad]);

  const handleSaveToggle = async () => {
    if (!currentUser) {
      setLoginWarn(true);
      return;
    }
    if (!ad) return;
    try {
      if (saved) {
        await axios.post('/ads/unsave/', { ad_id: ad.id });
      } else {
        await axios.post('/ads/save/', { ad_id: ad.id });
      }
      const res = await axios.get(`/ads/${ad.id}/`);
      setAd({ ...res.data });
      if (res.data.saved_by && Array.isArray(res.data.saved_by)) {
        setSaved(res.data.saved_by.includes(currentUser.id));
      } else {
        setSaved(false);
      }
    } catch (error) {
      console.error('Error saving ad:', error);
    }
  };

  const handleNoteSave = async () => {
    if (!ad) return;
    if (!localStorage.getItem('token')) {
      setLoginWarn(true);
      return;
    }
    setNoteLoading(true);
    try {
      if (noteId) {
        await axios.patch(`/ad-notes/${noteId}/`, { content: note });
      } else {
        const res = await axios.post(`/ad-notes/`, { ad: ad.id, content: note });
        setNoteId(res.data.id);
      }
      setNoteError(null);
    } catch {
      setNoteError('خطا در ذخیره یادداشت');
    }
    setNoteLoading(false);
  };

  const images = Array.isArray(ad?.images) ? ad.images : [];

  if (!ad) return <Typography>در حال بارگذاری...</Typography>;

  return (
    <Box sx={{
      display: 'flex',
      flexDirection: { xs: 'column', md: 'row' },
      gap: 3,
      justifyContent: 'center',
      alignItems: 'flex-start',
      width: '100%',
      mt: 4,
      px: { xs: 0, md: 0 }
    }}>
      <Card sx={{
        flex: 1,
        borderRadius: 3,
        boxShadow: 3,
        direction: 'rtl',
        border: 1,
        borderColor: 'grey.100',
        p: 2,
        minWidth: 0,
        position: 'relative',
        mx: { xs: 'auto', md: 0 },
        width: { xs: 'calc(86vw - 10px)', md: 'auto' },
        maxWidth: { xs: '100vw', md: 600 },
      }}>
        <Box sx={{ position: 'absolute', top: 16, right: 16, zIndex: 10, display: 'flex', flexDirection: 'column', gap: 1 }}>
          <IconButton
            sx={{ bgcolor: 'white', borderRadius: 99, boxShadow: 1 }}
            onClick={handleSaveToggle}
            aria-label={saved ? 'حذف از نشان‌شده‌ها' : 'نشان کردن'}
          >
            {saved ? <BookmarkIcon color="primary" /> : <BookmarkBorderIcon color="action" />}
          </IconButton>
          {currentUser?.id === ad?.user?.id && (
            <>
              <IconButton
                sx={{ bgcolor: 'white', borderRadius: 99, boxShadow: 1, mt: 1, color: 'grey.500' }}
                onClick={() => navigate(`/post-ad?edit=${ad.id}`)}
                aria-label="ویرایش آگهی"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M3 17.25V21h3.75l11.06-11.06-3.75-3.75L3 17.25zM20.71 7.04a1.003 1.003 0 0 0 0-1.42l-2.34-2.34a1.003 1.003 0 0 0-1.42 0l-1.83 1.83 3.75 3.75 1.84-1.82z" fill="currentColor"/>
                </svg>
              </IconButton>
              <IconButton
                sx={{ bgcolor: 'white', borderRadius: 99, boxShadow: 1, mt: 1, color: 'grey.500' }}
                onClick={() => setShowDeleteConfirm(true)}
                aria-label="حذف آگهی"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M6 19C6 20.1 6.9 21 8 21H16C17.1 21 18 20.1 18 19V7H6V19ZM19 4H15.5L14.79 3.29C14.61 3.11 14.35 3 14.09 3H9.91C9.65 3 9.39 3.11 9.21 3.29L8.5 4H5V6H19V4Z" fill="currentColor"/>
                </svg>
              </IconButton>
            </>
          )}
        </Box>

        {showDeleteConfirm && (
          <Box sx={{
            position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
            bgcolor: 'rgba(0,0,0,0.25)', zIndex: 9999, display: 'flex',
            alignItems: 'center', justifyContent: 'center'
          }}>
            <Box sx={{ bgcolor: 'white', borderRadius: 3, boxShadow: 6, p: 3, minWidth: 320, textAlign: 'center' }}>
              <Typography variant="subtitle1" color="primary" fontWeight={700} gutterBottom>
                آیا مطمئن هستید که می‌خواهید این آگهی را حذف کنید؟
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 2 }}>
                <Button
                  variant="contained"
                  color="error"
                  onClick={async () => {
                    setDeleteLoading(true);
                    try {
                      await axios.delete(`/ads/${ad.id}/`);
                      setShowDeleteConfirm(false);
                      navigate('/');
                    } catch {}
                    setDeleteLoading(false);
                  }}
                  disabled={deleteLoading}
                  sx={{ borderRadius: 2, bgcolor: '#c03f1fff', color: 'white', '&:hover': { bgcolor: '#cf8e7e' } }}
                >
                  حذف آگهی
                </Button>
                <Button variant="outlined" onClick={() => setShowDeleteConfirm(false)} sx={{ borderRadius: 2 }}>
                  انصراف
                </Button>
              </Box>
            </Box>
          </Box>
        )}

        {/* Image section — click to open lightbox */}
        <Box sx={{ position: 'relative', mb: 2, cursor: images.length > 0 ? 'zoom-in' : 'default' }}
          onClick={() => { if (images.length > 0) setLightboxOpen(true); }}
        >
          <Box
            component="img"
            height={320}
            src={images.length > 0 ? images[imgIdx]?.image : ''}
            alt={ad.title}
            sx={{ width: '100%', borderRadius: 2, objectFit: 'cover', bgcolor: 'grey.50', display: 'block' }}
          />
          {images.length > 1 && (
            <Box sx={{ position: 'absolute', bottom: 8, left: 0, right: 0, display: 'flex', justifyContent: 'center', gap: 1 }}>
              {images.map((img, idx) => (
                <Box
                  key={img.id}
                  sx={{ width: 14, height: 14, borderRadius: '50%', bgcolor: imgIdx === idx ? 'primary.main' : 'grey.300', cursor: 'pointer' }}
                  onClick={e => { e.stopPropagation(); setImgIdx(idx); }}
                />
              ))}
            </Box>
          )}
        </Box>

        {/* Fullscreen lightbox */}
        {lightboxOpen && (
          <Box
            sx={{
              position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
              bgcolor: 'rgba(0,0,0,0.92)', zIndex: 10000,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
            onClick={() => setLightboxOpen(false)}
          >
            <Box
              component="img"
              src={images[imgIdx]?.image}
              alt={ad.title}
              sx={{ maxWidth: '90vw', maxHeight: '90vh', objectFit: 'contain', borderRadius: 2, boxShadow: 8 }}
              onClick={e => e.stopPropagation()}
            />
            {/* Close button */}
            <IconButton
              onClick={() => setLightboxOpen(false)}
              sx={{ position: 'absolute', top: 16, right: 16, color: 'white', bgcolor: 'rgba(255,255,255,0.15)', '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' } }}
            >
              <CloseIcon />
            </IconButton>
            {/* Image counter */}
            {images.length > 1 && (
              <Typography sx={{ position: 'absolute', bottom: 24, left: '50%', transform: 'translateX(-50%)', color: 'white', bgcolor: 'rgba(0,0,0,0.4)', px: 2, py: 0.5, borderRadius: 4, fontSize: 14 }}>
                {toFarsiNumber(imgIdx + 1)} / {toFarsiNumber(images.length)}
              </Typography>
            )}
            {/* Navigation arrows */}
            {images.length > 1 && (
              <>
                <IconButton
                  onClick={e => { e.stopPropagation(); setImgIdx(i => (i - 1 + images.length) % images.length); }}
                  sx={{ position: 'absolute', right: 16, top: '50%', transform: 'translateY(-50%)', color: 'white', bgcolor: 'rgba(255,255,255,0.15)', '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' } }}
                >
                  <ChevronRightIcon fontSize="large" />
                </IconButton>
                <IconButton
                  onClick={e => { e.stopPropagation(); setImgIdx(i => (i + 1) % images.length); }}
                  sx={{ position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)', color: 'white', bgcolor: 'rgba(255,255,255,0.15)', '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' } }}
                >
                  <ChevronLeftIcon fontSize="large" />
                </IconButton>
              </>
            )}
          </Box>
        )}

        <CardContent sx={{ p: 0 }}>
          <Typography variant="h5" fontWeight={800} color="primary" gutterBottom align="right">
            {toFarsiNumber(ad.title)}
          </Typography>
          {/* Task 5: whiteSpace pre-line renders newlines from description */}
          <Typography variant="body1" color="text.secondary" align="right" sx={{ mb: 2, whiteSpace: 'pre-line' }}>
            {toFarsiNumber(ad.description)}
          </Typography>
          {ad.category && (
            <Typography variant="subtitle2" color="info.main" align="right" sx={{ mb: 1, fontWeight: 600 }}>
              دسته‌بندی: {getCategoryPath(Number(ad.category))}
            </Typography>
          )}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mt: 1 }}>
            <Chip label={toFarsiNumber(ad.condition)} size="small" sx={{ bgcolor: 'blue.50', color: 'blue.700', fontWeight: 'bold', fontFamily: 'Vazirmatn' }} />
            {ad.category == 3 ? (
              <>
                <Chip label={`ودیعه: ${toFarsiPrice(ad.deposit)} تومان`} size="medium" sx={{ bgcolor: 'orange.50', color: 'orange.700', fontWeight: 'bold' }} />
                <Chip label={`اجاره: ${toFarsiPrice(ad.monthly_rent)} تومان`} size="medium" sx={{ bgcolor: 'green.50', color: 'green.700', fontWeight: 'bold' }} />
              </>
            ) : (
              <Chip label={toFarsiPrice(ad.price) + ' تومان'} size="medium" sx={{ bgcolor: 'green.50', color: 'green.700', fontWeight: 'bold' }} />
            )}
          </Box>

          {ad.custom_fields && Object.keys(ad.custom_fields).length > 0 && (
            <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {Object.entries(ad.custom_fields).map(([key, value]) => (
                <Chip
                  key={key}
                  label={`${toFarsiNumber(key)}: ${value === true ? 'دارد' : value === false ? 'ندارد' : (typeof value === 'number' ? toFarsiPrice(value) : toFarsiNumber(value))}`}
                  size="small"
                  sx={{ bgcolor: 'grey.100', color: 'grey.800', fontWeight: 'bold', fontFamily: 'Vazirmatn' }}
                />
              ))}
            </Box>
          )}

          <Typography variant="caption" color="text.disabled" align="right" sx={{ mt: 2, display: 'block' }}>
            {toFarsiNumber(getRelativeTime(ad.date_posted))}
          </Typography>

          {/* Note section */}
          <Box sx={{ mt: 2 }}>
            {!localStorage.getItem('token') ? (
              <Box
                onClick={() => setLoginWarn(true)}
                sx={{
                  p: 2, border: '1px dashed', borderColor: 'grey.300',
                  borderRadius: 2, cursor: 'pointer', textAlign: 'center',
                  color: 'text.secondary', userSelect: 'none',
                  '&:hover': { borderColor: 'primary.main', color: 'primary.main', bgcolor: 'grey.50' },
                }}
              >
                <Typography variant="body2">برای ثبت یادداشت ابتدا وارد شوید</Typography>
              </Box>
            ) : (
              <>
                <TextField
                  multiline
                  minRows={2}
                  maxRows={6}
                  value={note}
                  onChange={e => setNote(e.target.value)}
                  placeholder="یادداشت خود را برای این آگهی بنویسید..."
                  sx={{ width: '100%', bgcolor: 'white', borderRadius: 2, mb: 1 }}
                  disabled={noteLoading}
                />
                {(note !== originalNote && !noteLoading) && (
                  <Button
                    variant="contained"
                    size="small"
                    onClick={async () => {
                      await handleNoteSave();
                      setOriginalNote(note);
                    }}
                    disabled={noteLoading}
                    sx={{ borderRadius: 2 }}
                  >
                    ذخیره یادداشت
                  </Button>
                )}
                {noteError && <Typography color="error" variant="caption">{noteError}</Typography>}
              </>
            )}
          </Box>
        </CardContent>
      </Card>

      <Box sx={{
        width: { xs: '100%', md: 400 },
        minWidth: { xs: 0, md: 320 },
        flexShrink: 0,
        mt: 0,
        alignSelf: { xs: 'stretch', md: 'flex-start' }
      }}>
        <ChatSection adId={ad?.id} ad={ad} currentUser={currentUser} currentUserId={currentUser?.id} />
      </Box>

      <Snackbar
        open={loginWarn}
        onClose={() => setLoginWarn(false)}
        message="برای این عملیات ابتدا وارد حساب کاربری شوید"
        severity="warning"
        autoHideDuration={3500}
      />
    </Box>
  );
}

export default AdDetailsPage;
